import { canonicalJson, hashCanonicalJson } from '../reportGeneration/reportContractHash';
import { demonstrationDocumentProvenanceV1Schema } from '../../types/demonstration';

export const PROVIDER_AUTHORITY_MATERIAL_SCHEMA_VERSION = 'provider-authority-material-v1' as const;

export interface ProviderAuthorityMaterialIdentityV1 {
  schemaVersion: typeof PROVIDER_AUTHORITY_MATERIAL_SCHEMA_VERSION;
  contentHash: string;
}

/**
 * Collection/UI metadata never supplies a source fact. These exclusions are
 * exact paths, not a recursive timestamp rule: claim.lastWrittenAt determines
 * authored-evidence precedence, and source/expiration dates remain evidence.
 * Every unknown field and every source body/identity/qualification is retained.
 */
const DOCUMENT_BOOKKEEPING_KEYS = new Set([
  'relevanceUpdatedAt', 'autoTaggedAt',
  'pdfTextReprocessedAt', 'officeReprocessedAt', 'extractionStatusAt',
  'preview', 'folder', 'movedAt',
]);

function record(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function withoutKeys(value: unknown, excluded: ReadonlySet<string>): unknown {
  if (!record(value)) return value;
  const result = Object.create(null) as Record<string, unknown>;
  for (const key of Object.keys(value)) {
    if (!excluded.has(key)) result[key] = value[key];
  }
  return result;
}

function projectDocument(value: unknown): unknown {
  const projected = withoutKeys(value, DOCUMENT_BOOKKEEPING_KEYS);
  if (!record(projected)) return projected;
  // Compact site records may carry only a source preview. An extraction
  // revision can then change unseen text while preserving the raw PDF hash.
  // Keep that revision unless the full captured body (or its maintained
  // extracted-body provenance digest) is separately bound by this payload.
  const provenance = demonstrationDocumentProvenanceV1Schema.safeParse(projected.demonstrationProvenance);
  const bodyIsBound = typeof projected.fullExtractedText === 'string'
    || provenance.success;
  if (bodyIsBound) delete projected.updatedAt;
  if (Object.hasOwn(projected, 'topics')) {
    projected.topics = withoutKeys(projected.topics, new Set(['segmentedAt']));
  }
  if (Object.hasOwn(projected, 'generationScreeningExtraction')) {
    projected.generationScreeningExtraction = withoutKeys(projected.generationScreeningExtraction, new Set(['extractedAt']));
  }
  if (Object.hasOwn(projected, 'extractionOutcome')) {
    projected.extractionOutcome = withoutKeys(projected.extractionOutcome, new Set(['at']));
  }
  return projected;
}

function projectContext(value: unknown): unknown {
  const projected = withoutKeys(value, new Set(['updatedAt', 'extractedAt']));
  if (!record(projected) || !record(projected.docContextScans)) return projected;
  const scans = Object.create(null) as Record<string, unknown>;
  for (const key of Object.keys(projected.docContextScans)) {
    scans[key] = withoutKeys(projected.docContextScans[key], new Set(['at']));
  }
  projected.docContextScans = scans;
  return projected;
}

/**
 * A conservative semantic view of the EXACT provider authority selected by
 * generation/currentness. It does not infer relevance, drop documents or
 * claims, reorder sources, truncate text, or reinterpret evidence roles.
 * Raw authority hashing remains separate for immutable/legacy verification.
 * uploadedAt remains bound because a digest-matched replacement may be
 * selected by recency when its original source document ID is unavailable.
 */
export function providerAuthorityMaterialPayload(value: unknown): unknown {
  // Use the same JSON projection as immutable contract hashing. This clones
  // rather than mutating the selected source, rejects cycles and preserves
  // own '__proto__' data keys through the null-prototype projections above.
  const projected: unknown = JSON.parse(canonicalJson(value));
  if (!record(projected)) return projected;
  if (Object.hasOwn(projected, 'siteContext')) {
    projected.siteContext = projectContext(projected.siteContext);
  }
  if (Array.isArray(projected.documents)) {
    projected.documents = projected.documents.map(projectDocument);
  }
  return projected;
}

export function buildProviderAuthorityMaterialIdentity(value: unknown): ProviderAuthorityMaterialIdentityV1 {
  return {
    schemaVersion: PROVIDER_AUTHORITY_MATERIAL_SCHEMA_VERSION,
    contentHash: hashCanonicalJson(providerAuthorityMaterialPayload(value)),
  };
}
